import { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeftRight, Building2, Check, ChevronLeft, ChevronRight, MapPin, Save, SkipForward, Users } from 'lucide-react';
import type { CountyResearchRecord } from './types';

type NamePrefix = '' | 'none' | 'Mr.' | 'Mrs.' | 'Mr. and Mrs.' | 'Ms.';

interface CleanedRecord {
  record: CountyResearchRecord;
  prefix: NamePrefix;
  firstName: string;
  lastName: string;
  suffix: string;
  isCouple: boolean;
  skipped: boolean;
  reviewed: boolean;
  isBusiness: boolean;
  businessName: string;
  street: string;
  city: string;
  state: string;
  zip: string;
}

interface NameCleanupWizardProps {
  open: boolean;
  onClose: () => void;
  records: CountyResearchRecord[];
  getSellerName: (r: CountyResearchRecord) => string;
  getSellerAddress: (r: CountyResearchRecord) => string;
  onComplete: (cleaned: CleanedRecord[]) => void;
  onSaveProgress?: (cleaned: CleanedRecord[]) => void;
}

// Common male first names for auto-detection
const MALE_NAMES = new Set([
  'james','john','robert','michael','william','david','richard','joseph','thomas','charles',
  'christopher','daniel','matthew','anthony','mark','donald','steven','paul','andrew','joshua',
  'kenneth','kevin','brian','george','timothy','ronald','edward','jason','jeffrey','ryan',
  'jacob','gary','nicholas','eric','jonathan','stephen','larry','justin','scott','brandon',
  'benjamin','samuel','raymond','gregory','frank','alexander','patrick','jack','dennis','jerry',
  'tyler','aaron','jose','adam','nathan','henry','peter','zachary','douglas','harold',
  'carl','arthur','gerald','roger','keith','jeremy','terry','sean','austin','albert',
  'jesse','willie','bruce','ralph','gabriel','russell','bobby','philip','eugene','randy',
  'johnny','harry','wayne','roy','vincent','billy','joe','dale','alan','juan',
  'curtis','luis','todd','lance','troy','travis','karl','kurt','rick','dean',
  'darryl','daryl','warren','chad','terry','gene','tim','tom','ted','don','dan','bob','jim',
  'mike','steve','chris','jeff','greg','tony','matt','dave','bill','ed','ben','sam','brad',
  'chuck','doug','fred','hank','herb','ivan','jake','kent','leon','lyle','marc','neal',
  'neil','nick','norm','omar','otis','owen','pete','phil','reed','reid','rene','rex','rick',
  'rod','ron','ross','rudy','russ','seth','stan','wade','walt','ward','zach','loren','lonnie'
]);

const FEMALE_NAMES = new Set([
  'mary','patricia','jennifer','linda','elizabeth','barbara','susan','jessica','sarah','karen',
  'nancy','lisa','margaret','betty','sandra','ashley','kimberly','emily','donna','michelle',
  'carol','amanda','melissa','deborah','stephanie','dorothy','rebecca','sharon','laura','cynthia',
  'kathleen','amy','shirley','angela','helen','anna','brenda','pamela','nicole','samantha',
  'katherine','christine','debra','rachel','catherine','carolyn','janet','ruth','maria','heather',
  'diane','virginia','julie','joyce','victoria','kelly','christina','joan','evelyn','lauren',
  'judith','olivia','frances','martha','cheryl','megan','andrea','hannah','jacqueline','ann',
  'jean','alice','kathryn','gloria','teresa','doris','sara','janice','julia','marie',
  'madison','grace','judy','theresa','beverly','denise','marilyn','amber','danielle','rose',
  'brittany','diana','abigail','natalie','jane','lori','tiffany','alexis','kayla','sherry',
  'crystal','wendy','tina','holly','erin','tammy','tracy','stacy','stacey','robin','peggy',
  'tara','jill','kim','kris','sue','deb','pam','beth','joy','fay','jan','lynn','carla','lynda'
]);


function detectCouple(rawName: string): { isCouple: boolean; name1: string; name2: string } {
  const normalized = rawName.replace(/\s+/g, ' ').trim();
  
  // Patterns: "COLE, MARK D and COLE, MICHELE K" or "MARK & MICHELE COLE"
  const andPatterns = [/ and /i, / & /i, / \+ /i];
  
  for (const pattern of andPatterns) {
    if (pattern.test(normalized)) {
      const parts = normalized.split(pattern);
      if (parts.length === 2) {
        return { isCouple: true, name1: parts[0].trim(), name2: parts[1].trim() };
      }
    }
  }
  
  return { isCouple: false, name1: normalized, name2: '' };
}

const PREFIX_TOKENS = new Set(['mr', 'mrs', 'ms', 'mr.', 'mrs.', 'ms.', 'dr', 'dr.']);

function extractSavedNameParts(rawName: string): { name: string; prefix: NamePrefix; suffix: string } {
  let name = rawName.replace(/\s+/g, ' ').trim();
  let prefix: NamePrefix = 'none';
  let suffix = '';

  const prefixPatterns: Array<{ pattern: RegExp; value: NamePrefix }> = [
    { pattern: /^Mr\.?\s+(?:and|&)\s+Mrs\.?\s+/i, value: 'Mr. and Mrs.' },
    { pattern: /^Mrs\.?\s+/i, value: 'Mrs.' },
    { pattern: /^Mr\.?\s+/i, value: 'Mr.' },
    { pattern: /^Ms\.?\s+/i, value: 'Ms.' },
  ];

  for (const { pattern, value } of prefixPatterns) {
    if (pattern.test(name)) {
      prefix = value;
      name = name.replace(pattern, '').trim();
      break;
    }
  }

  // Saved/cleaned records are proper-cased and store suffix after a comma.
  // Do not treat raw courthouse "LAST, FIRST" names as suffixes.
  if ((/[a-z]/.test(name) || prefix !== 'none') && name.includes(',')) {
    const [namePart, ...suffixParts] = name.split(',');
    name = namePart.trim();
    suffix = suffixParts.join(',').trim();
  }

  return { name, prefix, suffix };
}

function parseNamePart(raw: string): { first: string; last: string } {
  const cleaned = raw.replace(/\s+/g, ' ').trim();
  if (!cleaned) return { first: '', last: '' };

  // Detect if name is already cleaned (proper case, not ALL CAPS courthouse format).
  // Courthouse raw data is essentially always ALL CAPS. If the input has lowercase
  // letters, treat it as already-cleaned "First [Middle] Last" order and do not swap.
  const alreadyCleaned = /[a-z]/.test(cleaned);

  // Handle "LAST, FIRST MIDDLE" format (only for uncleaned courthouse data).
  if (!alreadyCleaned && cleaned.includes(',')) {
    const [lastPart, ...rest] = cleaned.split(',');
    const firstParts = rest.join(',').trim().split(/\s+/);
    return {
      last: toProperCase(lastPart.trim()),
      first: toProperCase(firstParts[0] || ''),
    };
  }

  // Strip any leading prefix tokens (Mr., Mrs., etc.) that may have been saved.
  const rawParts = cleaned.split(/\s+/).filter(p => !PREFIX_TOKENS.has(p.toLowerCase().replace(/\.$/, '') + (p.endsWith('.') ? '.' : '')) && !PREFIX_TOKENS.has(p.toLowerCase()));
  const parts = rawParts.length ? rawParts : cleaned.split(/\s+/);
  if (parts.length === 1) return { first: '', last: toProperCase(parts[0]) };

  if (alreadyCleaned) {
    // Already-cleaned "First [Middle] Last" — keep first token as first name, last token as last name.
    return { first: toProperCase(parts[0]), last: toProperCase(parts[parts.length - 1]) };
  }

  // Courthouse index format is typically "LAST FIRST [MIDDLE]" (no comma).
  // Drop middle names/initials: any single-char token.
  const significantParts = parts.filter(p => p.replace(/\./g, '').length > 1);
  if (significantParts.length === 0) return { first: '', last: toProperCase(parts[0]) };
  if (significantParts.length === 1) return { first: '', last: toProperCase(significantParts[0]) };
  const tokenA = significantParts[0];
  const tokenB = significantParts[significantParts.length - 1];
  const aIsFirst = MALE_NAMES.has(tokenA.toLowerCase()) || FEMALE_NAMES.has(tokenA.toLowerCase());
  const bIsFirst = MALE_NAMES.has(tokenB.toLowerCase()) || FEMALE_NAMES.has(tokenB.toLowerCase());

  // If the first token is a known first name and the last isn't, it's "FIRST LAST".
  // Otherwise default to county-records order "LAST FIRST".
  if (aIsFirst && !bIsFirst) {
    return { first: toProperCase(tokenA), last: toProperCase(tokenB) };
  }
  return { first: toProperCase(tokenB), last: toProperCase(tokenA) };
}

function toProperCase(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function pickMaleName(name1: { first: string; last: string }, name2: { first: string; last: string }): { first: string; last: string } {
  const n1Male = MALE_NAMES.has(name1.first.toLowerCase());
  const n2Male = MALE_NAMES.has(name2.first.toLowerCase());
  
  if (n1Male && !n2Male) return name1;
  if (n2Male && !n1Male) return name2;
  // Default to first name if can't determine
  return name1;
}

const BUSINESS_KEYWORDS = /\b(LLC|L\.L\.C|INC|CORP|TRUST|LTD|LP|L\.P|COMPANY|ENTERPRISES|HOLDINGS|PROPERTIES|INVESTMENTS|BANK|MORTGAGE|LENDING|PARTNERS|PARTNERSHIP|ASSOCIATION|FOUNDATION|CREDIT\s+UNION)\b/i;

// Detect trust patterns: "Name and Name, Trustees of The ... Trust ..."
const TRUST_COUPLE_PATTERN = /^(.+?),?\s*Trustees?\s+of\s+/i;
const TRUST_SUFFIX_PATTERN = /,?\s*(Trustees?|Successor\s+Trustees?)\s+(of\s+.+)/i;

function extractTrustInfo(rawName: string): { namesPart: string; suffix: string; isTrust: boolean } {
  // Match patterns like "Name and Name, Trustees of The XYZ Trust Dated ..."
  const trusteeMatch = rawName.match(TRUST_COUPLE_PATTERN);
  if (trusteeMatch) {
    return {
      namesPart: trusteeMatch[1].trim(),
      suffix: 'Trustees',
      isTrust: true,
    };
  }
  return { namesPart: rawName, suffix: '', isTrust: false };
}

const STATE_ABBREVIATIONS: Record<string, string> = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
  'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
  'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
};

const US_STATE_CODES = new Set(Object.values(STATE_ABBREVIATIONS));

function parseRawAddress(raw: string): { street: string; city: string; state: string; zip: string } {
  if (!raw || !raw.trim()) return { street: '', city: '', state: '', zip: '' };

  let clean = raw
    .replace(/,?\s*County of [^,]+/gi, '')
    .replace(/,?\s*State of [^,]+/gi, '')
    .replace(/\s*\(during the months[^)]*\)/gi, '')
    .trim();

  const parts = clean.split(',').map(p => p.trim());
  if (parts.length < 2) return { street: clean, city: '', state: '', zip: '' };

  const street = parts[0];
  let state = '', zip = '', city = '';
  const lastPart = parts[parts.length - 1];

  // Match "State ZIP" or "ST ZIP"
  const stateZipMatch = lastPart.match(/([A-Z][a-z]+|[A-Z]{2})\s+(\d{5}(-\d{4})?)/);
  if (stateZipMatch) {
    const stateRaw = stateZipMatch[1];
    state = STATE_ABBREVIATIONS[stateRaw] || (US_STATE_CODES.has(stateRaw) ? stateRaw : stateRaw);
    zip = stateZipMatch[2].split('-')[0];
    if (parts.length === 3) city = parts[1];
    else if (parts.length > 3) city = parts.slice(1, -1).join(', ');
    else city = lastPart.replace(stateZipMatch[0], '').trim();
  } else {
    // Try just zip at end
    const zipOnly = lastPart.match(/\b(\d{5})\b/);
    if (zipOnly) {
      zip = zipOnly[1];
      const remainder = lastPart.replace(zipOnly[0], '').trim();
      const stMatch = remainder.match(/\b([A-Z]{2})\b/);
      if (stMatch) state = stMatch[1];
      if (parts.length >= 3) city = parts.slice(1, -1).join(', ');
    } else {
      // Try state only
      const stOnly = lastPart.match(/\b([A-Z]{2})\s*$/);
      if (stOnly && US_STATE_CODES.has(stOnly[1])) {
        state = stOnly[1];
        if (parts.length === 3) city = parts[1];
        else if (parts.length > 3) city = parts.slice(1, -1).join(', ');
      } else if (parts.length === 2) {
        city = parts[1];
      } else {
        city = parts.slice(1).join(', ');
      }
    }
  }

  return { street: street.trim(), city: city.trim(), state: state.trim(), zip: zip.trim() };
}

function processRecord(record: CountyResearchRecord, getSellerName: (r: CountyResearchRecord) => string, getSellerAddress: (r: CountyResearchRecord) => string): CleanedRecord {
  const rawName = getSellerName(record);
  const savedNameParts = extractSavedNameParts(rawName);
  const nameToParse = savedNameParts.name || rawName;
  const rawAddress = getSellerAddress(record);
  const addr = parseRawAddress(rawAddress);
  const addrFields = { street: addr.street, city: addr.city, state: addr.state, zip: addr.zip };
  
  // First check for trust with couple pattern (before business keyword check)
  const trustInfo = extractTrustInfo(nameToParse);
  
  if (trustInfo.isTrust) {
    const couple = detectCouple(trustInfo.namesPart);
    if (couple.isCouple) {
      const parsed1 = parseNamePart(couple.name1);
      const parsed2 = parseNamePart(couple.name2);
      if (!parsed2.last && parsed1.last) parsed2.last = parsed1.last;
      if (!parsed1.last && parsed2.last) parsed1.last = parsed2.last;
      const chosen = pickMaleName(parsed1, parsed2);
      return { record, prefix: savedNameParts.prefix !== 'none' ? savedNameParts.prefix : 'Mr. and Mrs.', firstName: chosen.first, lastName: chosen.last || parsed1.last || parsed2.last, suffix: savedNameParts.suffix || trustInfo.suffix, isCouple: true, skipped: false, reviewed: false, isBusiness: false, businessName: '', ...addrFields };
    }
    const parsed = parseNamePart(trustInfo.namesPart);
    return { record, prefix: savedNameParts.prefix, firstName: parsed.first, lastName: parsed.last, suffix: savedNameParts.suffix || trustInfo.suffix, isCouple: savedNameParts.prefix === 'Mr. and Mrs.', skipped: false, reviewed: false, isBusiness: false, businessName: '', ...addrFields };
  }
  
  if (BUSINESS_KEYWORDS.test(rawName)) {
    return { record, prefix: 'none' as NamePrefix, firstName: '', lastName: '', suffix: '', isCouple: false, skipped: false, reviewed: false, isBusiness: true, businessName: rawName.split(/\s+/).map(w => toProperCase(w)).join(' '), ...addrFields };
  }
  
  const couple = detectCouple(nameToParse);
  if (couple.isCouple) {
    const parsed1 = parseNamePart(couple.name1);
    const parsed2 = parseNamePart(couple.name2);
    if (!parsed2.last && parsed1.last) parsed2.last = parsed1.last;
    if (!parsed1.last && parsed2.last) parsed1.last = parsed2.last;
    const chosen = pickMaleName(parsed1, parsed2);
    return { record, prefix: savedNameParts.prefix !== 'none' ? savedNameParts.prefix : 'Mr. and Mrs.', firstName: chosen.first, lastName: chosen.last || parsed1.last || parsed2.last, suffix: savedNameParts.suffix, isCouple: true, skipped: false, reviewed: false, isBusiness: false, businessName: '', ...addrFields };
  }
  
  const parsed = parseNamePart(nameToParse);
  return { record, prefix: savedNameParts.prefix, firstName: parsed.first, lastName: parsed.last, suffix: savedNameParts.suffix, isCouple: savedNameParts.prefix === 'Mr. and Mrs.', skipped: false, reviewed: false, isBusiness: false, businessName: '', ...addrFields };

}

export function NameCleanupWizard({ open, onClose, records, getSellerName, getSellerAddress, onComplete, onSaveProgress }: NameCleanupWizardProps) {
  const [cleanedRecords, setCleanedRecords] = useState<CleanedRecord[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const firstNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && records.length > 0) {
      const processed = records.map(r => processRecord(r, getSellerName, getSellerAddress));
      setCleanedRecords(processed);
      setCurrentIndex(0);
    }
  }, [open, records]);

  useEffect(() => {
    if (open) {
      setTimeout(() => firstNameRef.current?.focus(), 100);
    }
  }, [currentIndex, open]);

  const current = cleanedRecords[currentIndex];
  const totalCount = cleanedRecords.length;
  const doneCount = cleanedRecords.filter(r => r.reviewed && !r.skipped).length;

  const updateCurrent = useCallback((updates: Partial<CleanedRecord>) => {
    setCleanedRecords(prev => prev.map((r, i) => i === currentIndex ? { ...r, reviewed: true, ...updates } : r));
  }, [currentIndex]);

  const markReviewed = useCallback(() => {
    setCleanedRecords(prev => prev.map((r, i) => i === currentIndex ? { ...r, reviewed: true } : r));
  }, [currentIndex]);

  const goNext = useCallback(() => {
    if (currentIndex < totalCount - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex, totalCount]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  const handleSwap = useCallback(() => {
    if (!current) return;
    updateCurrent({ firstName: current.lastName, lastName: current.firstName, reviewed: true });
  }, [current, updateCurrent]);

  const handleAcceptAndNext = useCallback(() => {
    markReviewed();
    goNext();
  }, [markReviewed, goNext]);

  const handleToggleBusiness = useCallback(() => {
    if (!current) return;
    if (current.isBusiness) {
      const savedNameParts = extractSavedNameParts(getSellerName(current.record));
      const parsed = parseNamePart(savedNameParts.name || getSellerName(current.record));
      updateCurrent({ isBusiness: false, businessName: '', prefix: savedNameParts.prefix, firstName: parsed.first, lastName: parsed.last, suffix: savedNameParts.suffix, reviewed: true });
    } else {
      const raw = getSellerName(current.record);
      const properName = raw.split(/\s+/).map(w => toProperCase(w)).join(' ');
      updateCurrent({ isBusiness: true, businessName: properName, prefix: 'none' as NamePrefix, firstName: '', lastName: '', suffix: '', reviewed: true });
    }
  }, [current, updateCurrent, getSellerName]);

  const handleSkip = useCallback(() => {
    updateCurrent({ skipped: true, reviewed: true });
    goNext();
  }, [updateCurrent, goNext]);

  const handleFinish = useCallback(() => {
    onComplete(cleanedRecords.filter(r => r.reviewed && !r.skipped));
  }, [cleanedRecords, onComplete]);

  const handleSaveProgress = useCallback(() => {
    if (onSaveProgress) {
      onSaveProgress(cleanedRecords.filter(r => r.reviewed && !r.skipped));
    }
    onClose();
  }, [cleanedRecords, onSaveProgress, onClose]);


  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      // Don't capture if typing in an input
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
      
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleAcceptAndNext();
      } else if (e.key === 'b' && !isInput) {
        e.preventDefault();
        handleToggleBusiness();
      } else if (e.key === 's' && !isInput) {
        e.preventDefault();
        handleSwap();
      } else if (e.key === 'ArrowLeft' && !isInput) {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight' && !isInput) {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, handleAcceptAndNext, handleSwap, handleToggleBusiness, goPrev, goNext]);

  if (!current) return null;

  const rawName = getSellerName(current.record);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Name Cleanup — Record {currentIndex + 1} of {totalCount}
          </DialogTitle>
          <DialogDescription>
            Review and fix names before export. <strong>Enter</strong> = accept & next · <strong>S</strong> = swap · <strong>B</strong> = business · <strong>←→</strong> = navigate
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Progress bar */}
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all"
              style={{ width: `${((currentIndex + 1) / totalCount) * 100}%` }}
            />
          </div>

          {/* Raw name & address display */}
          <div className="p-3 bg-muted rounded-lg">
            <div className="flex gap-4">
              <div className="flex-1">
                <p className="text-xs text-muted-foreground mb-1">Original Name</p>
                <p className="font-mono text-sm font-semibold">{rawName}</p>
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground mb-1">Original Address</p>
                <p className="font-mono text-sm font-semibold">{getSellerAddress(current.record) || <span className="text-muted-foreground italic">none</span>}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs">
                {current.record.county?.toUpperCase()} · {current.record.document_number}
              </Badge>
              {current.isCouple && (
                <Badge className="bg-accent text-accent-foreground text-xs">
                  Couple Detected
                </Badge>
              )}
              {current.isBusiness && (
                <Badge className="bg-accent text-accent-foreground text-xs">
                  Business Entity
                </Badge>
              )}
            </div>
          </div>

          {/* Editable fields */}
          {current.isBusiness ? (
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Business Name</label>
              <Input
                ref={firstNameRef}
                value={current.businessName}
                onChange={(e) => updateCurrent({ businessName: e.target.value })}
                className="text-base"
              />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-12 gap-3">
                {/* Prefix */}
                <div className="col-span-3">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Prefix</label>
                  <Select
                    value={current.prefix}
                    onValueChange={(v) => updateCurrent({ prefix: v as NamePrefix })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">(None)</SelectItem>
                      <SelectItem value="Mr.">Mr.</SelectItem>
                      <SelectItem value="Mrs.">Mrs.</SelectItem>
                      <SelectItem value="Mr. and Mrs.">Mr. and Mrs.</SelectItem>
                      <SelectItem value="Ms.">Ms.</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* First Name */}
                <div className="col-span-3">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">First Name</label>
                  <Input
                    ref={firstNameRef}
                    value={current.firstName}
                    onChange={(e) => updateCurrent({ firstName: e.target.value })}
                    className="text-base"
                  />
                </div>

                {/* Last Name */}
                <div className="col-span-3">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Last Name</label>
                  <Input
                    value={current.lastName}
                    onChange={(e) => updateCurrent({ lastName: e.target.value })}
                    className="text-base"
                  />
                </div>

                {/* Suffix */}
                <div className="col-span-3">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Suffix</label>
                  <Input
                    value={current.suffix}
                    onChange={(e) => updateCurrent({ suffix: e.target.value })}
                    placeholder="e.g. Trustees"
                    className="text-base"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Address fields */}
          <div>
            <div className="flex items-center gap-1 mb-2">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
              <label className="text-xs font-semibold text-muted-foreground">Mailing Address</label>
            </div>
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-5">
                <Input
                  value={current.street}
                  onChange={(e) => updateCurrent({ street: e.target.value })}
                  placeholder="Street"
                  className="h-8 text-sm"
                />
              </div>
              <div className="col-span-3">
                <Input
                  value={current.city}
                  onChange={(e) => updateCurrent({ city: e.target.value })}
                  placeholder="City"
                  className="h-8 text-sm"
                />
              </div>
              <div className="col-span-2">
                <Input
                  value={current.state}
                  onChange={(e) => updateCurrent({ state: e.target.value })}
                  placeholder="ST"
                  className="h-8 text-sm"
                  maxLength={2}
                />
              </div>
              <div className="col-span-2">
                <Input
                  value={current.zip}
                  onChange={(e) => updateCurrent({ zip: e.target.value })}
                  placeholder="ZIP"
                  className="h-8 text-sm"
                  maxLength={5}
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="p-3 border rounded-lg bg-background">
            <p className="text-xs text-muted-foreground mb-1">Mail Label Preview</p>
            <p className="text-base font-semibold">
              {current.isBusiness
                ? current.businessName
                : (() => {
                    const nameParts = [current.prefix === 'none' ? '' : current.prefix, current.firstName, current.lastName].filter(Boolean).join(' ');
                    return current.suffix ? `${nameParts}, ${current.suffix}` : nameParts;
                  })()}
            </p>
            {current.street && (
              <p className="text-sm text-muted-foreground">
                {current.street}{current.city ? `, ${current.city}` : ''}{current.state ? `, ${current.state}` : ''} {current.zip}
              </p>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goPrev} disabled={currentIndex === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <Button variant="outline" size="sm" onClick={handleSwap} className="gap-1" disabled={current.isBusiness}>
              <ArrowLeftRight className="h-4 w-4" />
              Swap (S)
            </Button>

            <Button
              variant={current.isBusiness ? "default" : "outline"}
              size="sm"
              onClick={handleToggleBusiness}
              className="gap-1"
            >
              <Building2 className="h-4 w-4" />
              {current.isBusiness ? 'Person' : 'Business (B)'}
            </Button>

            <Button variant="ghost" size="sm" onClick={handleSkip} className="gap-1 text-muted-foreground">
              <SkipForward className="h-4 w-4" />
              Skip
            </Button>

            <div className="flex-1" />

            <Button variant="secondary" size="sm" onClick={handleSaveProgress} className="gap-1">
              <Save className="h-4 w-4" />
              Save & Exit
            </Button>

            {currentIndex < totalCount - 1 ? (
              <Button onClick={handleAcceptAndNext} className="gap-1">
                <Check className="h-4 w-4" />
                Accept & Next (Enter)
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={handleFinish} className="gap-1 bg-primary hover:bg-primary/90">
                <Check className="h-4 w-4" />
                Finish & Export ({doneCount} records)
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export type { CleanedRecord };
